import { useLazyQuery } from '@apollo/client';
import { css } from '@emotion/react';
import { useEffect } from 'react';
import BhagCounterConfigsQuery from 'graphql/queries/bhagCounterConfigs.graphql';
import { BhagCounterConfigsQueryOutput } from 'types/types';
import { isValidConfigs } from './utils';
import { trackBhagLinkClicked } from '../../../util/tag-manager';
import { MotionSafe } from 'components/_shared/widgets/render-control';
import { Flex } from 'components/box';
import { Heading } from 'components/typography';
import BhagCounter from './counter';
import { CounterGrid, GradientLink, GradientSurface } from './styles';
import { BhagProps, WrapperProps } from './types';

const BhagContainer = ({
  showLearnMore,
  hideBackground,
  ...rest
}: BhagProps) => (
  <CounterGrid
    gridColumnGap={2}
    px={[2, 4]}
    py={4}
    overflow="hidden"
    justifyItems="center"
    /*
     * One full-width column. The 0 minimum (rather than the implicit `auto` of a
     * bare `1fr`) lets the track shrink below its content's min-content width, so
     * the wide counter is clipped by the grid's `overflow: hidden` instead of
     * forcing the grid to overflow.
     * Some more info on this:
     * https://stackoverflow.com/questions/52861086/why-does-minmax0-1fr-work-for-long-elements-while-1fr-doesnt
     * https://github.com/w3c/csswg-drafts/issues/1777
     * https://stackoverflow.com/questions/52785750/prevent-grid-area-from-expanding-causing-whole-page-to-scroll
     */
    gridTemplateColumns="minmax(0, 1fr)"
    width="100%"
    withBackground={!hideBackground}
  >
    <Heading
      fontSize={[3, 4]}
      color="white"
      fontWeight={900}
      textAlign="center"
      css={css`
        text-transform: uppercase;
      `}
    >
      We&apos;ve saved South Africans
    </Heading>
    <GradientSurface
      width="100%"
      borderRadius="50px"
      overflow="hidden"
      justifyContent="center"
      alignItems="center"
      boxShadow="0px 8px 12px 0px #00000040 inset"
    >
      <BhagCounter {...rest} />
    </GradientSurface>
    <Flex
      alignItems="center"
      justifyContent="center"
      css={css`
        gap: 10px;
      `}
    >
      <Heading
        fontSize={[3, 4]}
        color="white"
        fontWeight={900}
        textAlign="center"
        css={css`
          text-transform: uppercase;
        `}
      >
        And counting
      </Heading>
      {showLearnMore && (
        <GradientLink
          href="/big-deal-goal"
          py={2}
          borderRadius="50px"
          px={3}
          onClick={() => trackBhagLinkClicked()}
        >
          <Heading fontFamily="header" color="white" fontWeight={900}>
            Learn More
          </Heading>
        </GradientLink>
      )}
    </Flex>
  </CounterGrid>
);

const QueryLayer = ({ SSRConfigs, ...restProps }: WrapperProps) => {
  const [
    getCounterConfigs,
    { loading, data, error },
  ] = useLazyQuery<BhagCounterConfigsQueryOutput>(BhagCounterConfigsQuery);

  useEffect(() => {
    if (!data && !loading && !error && !isValidConfigs(SSRConfigs)) {
      getCounterConfigs();
    }
  }, [SSRConfigs, data, error, getCounterConfigs, loading]);

  const configs = isValidConfigs(SSRConfigs) ? SSRConfigs : data?.configs.bhag;

  if (!isValidConfigs(configs)) return null;

  return <BhagContainer {...configs} {...restProps} />;
};

const Wrapper = ({ SSRConfigs, ...restProps }: WrapperProps) => (
  <MotionSafe>
    <QueryLayer SSRConfigs={SSRConfigs} {...restProps} />
  </MotionSafe>
);

export default Wrapper;
